import { PTORecord } from "../../../models/PtoRecord";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { StatusBadge } from "../../StatusBadge/StatusBadge";
import { formatDate, formatDateTime } from "../../../utils/DateUtils";
import React, { useState } from "react";
import { Dialog } from "primereact/dialog";
import { UserSession } from "../../../models/UserSession";
import { CircleCancelFill, Star } from "../../../assets/icons";
import { CircleCheckFill } from "../../../assets/icons";
import { DoNotDisturbCircleFill } from "../../../assets/icons";
import styles from "./styles.module.css";
import S3ImagePreview from "../../S3ImagePreview";

function PtoList({ ptoList, session }: { ptoList?: PTORecord[] | null; session?: UserSession }) {
  const [selectedPto, setSelectedPto] = useState<PTORecord | null>(null);
  const renderCreatedDate = (val: PTORecord) => {
    return <span>{formatDate(val.created_at)}</span>;
  };

  const renderStatus = (val: PTORecord) => {
    if (val.created_by_signature_s3 && val.employee_signature_s3) {
      return <StatusBadge variant={"success"}>Completed</StatusBadge>;
    }

    if (val.created_by_signature_s3 && !val.employee_signature_s3) {
      return <StatusBadge variant={"warning"}>Employee required</StatusBadge>;
    }

    if (val.employee_signature_s3 && !val.created_by_signature_s3) {
      return <StatusBadge variant={"warning"}>Observer required</StatusBadge>;
    }

    if (!val.created_by_signature_s3 && !val.employee_signature_s3) {
      return <StatusBadge variant={"error"}>Incomplete</StatusBadge>;
    }
  };

  if (!ptoList) {
    return <></>;
  }

  return (
    <>
      <DataTable
        value={ptoList}
        tableStyle={{ minWidth: "50rem" }}
        onRowClick={val => {
          setSelectedPto(val.data as PTORecord);
        }}
        selectionMode="single"
        paginator
        showGridlines
        rows={10}
      >
        <Column field={"topic_name"} header={"Topic"} style={{ minWidth: "12rem" }} />
        <Column
          field={"created_at"}
          header={"Created at"}
          body={renderCreatedDate}
          style={{ minWidth: "12rem" }}
          sortable
        />
        <Column field={"employee_fullname"} header={"Employee"} style={{ minWidth: "12rem" }} />
        <Column
          field={"status"}
          header={"Signature Status"}
          body={renderStatus}
          style={{ minWidth: "12rem" }}
        />
      </DataTable>
      {selectedPto && (
        <Dialog
          visible={!!selectedPto}
          header={`PTO Details: ${selectedPto.topic_name} - ${formatDate(selectedPto.created_at)}`}
          onHide={() => setSelectedPto(null)}
          style={{ width: "50vw" }}
        >
          <div className={"my-4"}>
            <div className={"custom-card-start-align"}>
              <p className={"card-heading"}>
                {selectedPto.site === session?.site_id && session.site_name}
              </p>

              <div className={"grid-2"}>
                <div className={"key-value"}>
                  <label className={"font-bold mr-2"}>Date & Time:</label>
                  <span>{formatDateTime(selectedPto?.created_at)}</span>
                </div>
                <div className={"key-value"}>
                  <label className={"font-bold mr-2"}>Updated at:</label>
                  <span>{formatDateTime(selectedPto?.updated_at)}</span>
                </div>
                <div className={"key-value"}>
                  <label className={"font-bold mr-2"}>Employee:</label>
                  <span>{selectedPto.employee_fullname}</span>
                </div>
                <div className={"key-value"}>
                  <label className={"font-bold mr-2"}>Observer:</label>
                  <span>{selectedPto.created_by_fullname}</span>
                </div>
              </div>
            </div>
            <div className={"custom-card-start-align"} style={{ marginTop: "1rem" }}>
              <p className={"card-heading"}>Observation checklist</p>
              {selectedPto.questions.map((question, index) => {
                return (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      alignItems: "start",
                      gap: "1rem",
                      marginBottom: "1.25rem",
                    }}
                  >
                    {question.value === "yes" ? (
                      <CircleCheckFill fill={"#2dd36f"} />
                    ) : question.value === "no" ? (
                      <CircleCancelFill fill={"#eb445a"} />
                    ) : (
                      <DoNotDisturbCircleFill fill={"#92949c"} />
                    )}

                    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                      <span style={{ fontWeight: "600" }}>{question.question}</span>
                      <span>{question.comment}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className={"custom-card-start-align"} style={{ marginTop: "1rem" }}>
              <p className={"card-heading"}>Overall rating</p>
              <div className={styles["rating-container"]}>
                {[1, 2, 3, 4, 5].map(rating => {
                  return (
                    <div key={rating}>
                      <Star
                        width={32}
                        height={32}
                        fill={
                          selectedPto?.overall_rating !== undefined &&
                          rating <= selectedPto.overall_rating
                            ? "#fad502"
                            : "#aeaeae"
                        }
                      />
                    </div>
                  );
                })}
              </div>
              <div className={"key-value"}>
                <label className={"font-bold mr-2"}>Comments:</label>
                {selectedPto.comment && selectedPto.comment.length > 0
                  ? selectedPto.comment
                  : "No additional comments made."}
              </div>
            </div>
            <div className={"custom-card-start-align"} style={{ marginTop: "1rem" }}>
              <p className={"card-heading"}>Signatures</p>
              <div className={"grid-2"}>
                <div>
                  Observer Signature:
                  {selectedPto.created_by_signature_s3 ? (
                    <S3ImagePreview
                      path={selectedPto.created_by_signature_s3}
                      setGlobalLoader={() => {}}
                      allowUpload={false}
                    />
                  ) : (
                    <div style={{ height: "30px" }}>No signature</div>
                  )}
                </div>
                <div>
                  Employee Signature:
                  {selectedPto.employee_signature_s3 ? (
                    <>
                      <S3ImagePreview
                        path={selectedPto.employee_signature_s3}
                        setGlobalLoader={() => {}}
                        allowUpload={false}
                      />
                    </>
                  ) : (
                    <div style={{ height: "30px" }}>No signature</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
}

export { PtoList };
